import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { Card, CardContent } from '../../components/ui/Card';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { format } from 'date-fns';

const { FiShoppingBag, FiPackage, FiClock, FiCheck, FiChevronRight } = FiIcons;

export const SupplierOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: supplier } = await supabase
      .from('suppliers_1739200000000')
      .select('id')
      .eq('owner_id', user.id)
      .single();

    if (!supplier) return;

    // Fetch order items for this supplier, joining with the parent order
    const { data, error } = await supabase
      .from('order_items_1739200000000')
      .select(`
        *,
        orders:order_id (
          id,
          status,
          created_at,
          buyer:buyer_id (
            full_name,
            company_name
          )
        ),
        product:product_id (
          name,
          image_url
        )
      `)
      .eq('supplier_id', supplier.id)
      .order('created_at', { ascending: false });

    if (data) {
      // Group items by order ID for better visualization
      const grouped = data.reduce((acc, item) => {
        const orderId = item.order_id;
        if (!acc[orderId]) {
          acc[orderId] = {
            ...item.orders,
            items: [],
            total: 0
          };
        }
        acc[orderId].items.push(item);
        acc[orderId].total += item.total_price;
        return acc;
      }, {});
      setOrders(Object.values(grouped));
    }
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'processing': return 'bg-blue-50 text-blue-700 border-blue-100';
      case 'cancelled': return 'bg-red-50 text-red-700 border-red-100';
      default: return 'bg-amber-50 text-amber-700 border-amber-100';
    }
  };

  if (loading) return <div className="p-8 text-center text-zinc-500">Loading incoming orders...</div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900">Incoming Orders</h1>
        <p className="text-zinc-500">Manage your active fulfillment and order history</p>
      </div>

      {orders.length === 0 ? (
        <Card className="py-20">
          <div className="text-center">
            <div className="w-16 h-16 bg-zinc-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiShoppingBag} className="w-8 h-8 text-zinc-200" />
            </div>
            <h3 className="text-lg font-bold text-zinc-900">No orders yet</h3>
            <p className="text-zinc-500 max-w-xs mx-auto">Once buyers purchase your products, they will appear here.</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <motion.div key={order.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
              <Card className="hover:border-zinc-300 transition-colors cursor-pointer group">
                <CardContent className="p-6">
                  <div className="flex flex-wrap items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center text-white">
                        <SafeIcon icon={FiPackage} className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-zinc-900">Order #{order.id.slice(0, 8)}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                        </div>
                        <p className="text-sm text-zinc-500">
                          From <span className="font-medium text-zinc-900">{order.buyer?.company_name || 'Individual Buyer'}</span>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-12">
                      <div className="hidden md:block">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Date</p>
                        <p className="text-sm font-medium">{format(new Date(order.created_at), 'MMM d, p')}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Total</p>
                        <p className="text-sm font-bold">€{order.total.toFixed(2)}</p>
                      </div>
                      <div className="w-8 h-8 rounded-full border border-zinc-200 flex items-center justify-center group-hover:bg-zinc-900 group-hover:border-zinc-900 group-hover:text-white transition-all">
                        <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-zinc-50 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {order.items.map((item) => (
                      <div key={item.id} className="flex items-center gap-3">
                        <img src={item.product?.image_url} className="w-10 h-10 rounded-lg object-cover" />
                        <div>
                          <p className="text-sm font-bold text-zinc-900">{item.product?.name}</p>
                          <p className="text-xs text-zinc-500">{item.quantity} units @ €{item.unit_price}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};